import React from "react";
import { CreateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, StyleProps } from "@hex-insights/core";
import { Form, FormState, SubmitButton, useInternalField } from "@hex-insights/forms";
import { Guardian, RelationshipCreateMutation, Student } from "../../../Utilities";
import { RelationshipFormState, RelationshipFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import { useCreateFormControl, useCreateFormSubmission } from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateOnStudentProps = Omit<
	CreateOnStudentProps<RelationshipCreateMutation["createRelationship"]>,
	"formState" | "create"
> & {
	formStateArgs?: RelationshipFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Relationship model using an internally managed form state.
 */
export function ControlledCreateOnStudent({ studentID, formStateArgs, ...props }: ControlledCreateOnStudentProps) {
	const fullFormStateArgs = React.useMemo(() => {
		if (!formStateArgs) {
			return {
				initialFormValues: {
					...RelationshipFormValues.initialCreate,
					studentID,
				},
			};
		}
		return {
			...formStateArgs,
			initialFormValues: {
				...RelationshipFormValues.initialCreate,
				...formStateArgs.initialFormValues,
				studentID,
			},
		};
	}, [studentID, formStateArgs]);
	const { formState, create } = useCreateFormControl(fullFormStateArgs);

	return <CreateOnStudent studentID={studentID} formState={formState} create={create} {...props} />;
}

export type CreateOnStudentProps<T> = {
	studentID: Student["id"];
	formState: FormState<RelationshipFormValues.Create>;
	create: CreateFunction<RelationshipFormValues.Create, T>;
	onSuccess: (formState: FormState<RelationshipFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<RelationshipFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Relationship model using the given form state.
 */
export function CreateOnStudent<T>({
	studentID,
	formState,
	create,
	onSuccess,
	formNameSuffix,
	...props
}: CreateOnStudentProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = `relationship.create.${studentID}` + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalField(formState, "studentID");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<Field.Guardian.Select formState={formState} withCreate />
				<Field.RelationshipType formState={formState} />
				<Field.IsPrimary formState={formState} />
				<Field.IsFinancialContact formState={formState} />
				<Field.IsEmergencyContact formState={formState} />
				<Field.IsAuthorizedForPickup formState={formState} />
				<Field.LivesWithStudent formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

export type ControlledCreateOnGuardianProps = Omit<
	CreateOnGuardianProps<RelationshipCreateMutation["createRelationship"]>,
	"formState" | "create"
> & {
	formStateArgs?: RelationshipFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Relationship model using an internally managed form state.
 */
export function ControlledCreateOnGuardian({ guardianID, formStateArgs, ...props }: ControlledCreateOnGuardianProps) {
	const fullFormStateArgs = React.useMemo(() => {
		if (!formStateArgs) {
			return {
				initialFormValues: {
					...RelationshipFormValues.initialCreate,
					guardianID,
				},
			};
		}
		return {
			...formStateArgs,
			initialFormValues: {
				...RelationshipFormValues.initialCreate,
				...formStateArgs.initialFormValues,
				guardianID,
			},
		};
	}, [guardianID, formStateArgs]);
	const { formState, create } = useCreateFormControl(fullFormStateArgs);

	return <CreateOnGuardian guardianID={guardianID} formState={formState} create={create} {...props} />;
}

export type CreateOnGuardianProps<T> = {
	guardianID: Guardian["id"];
	formState: FormState<RelationshipFormValues.Create>;
	create: CreateFunction<RelationshipFormValues.Create, T>;
	onSuccess: (formState: FormState<RelationshipFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<RelationshipFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Relationship model using the given form state.
 */
export function CreateOnGuardian<T>({
	guardianID,
	formState,
	create,
	onSuccess,
	formNameSuffix,
	...props
}: CreateOnGuardianProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = `relationship.create.${guardianID}` + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalField(formState, "guardianID");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<Field.Student.Select formState={formState} />
				<Field.RelationshipType formState={formState} />
				<Field.IsPrimary formState={formState} />
				<Field.IsFinancialContact formState={formState} />
				<Field.IsEmergencyContact formState={formState} />
				<Field.IsAuthorizedForPickup formState={formState} />
				<Field.LivesWithStudent formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}
